import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@lib/api';
import { useNotificationStore } from '@stores/notificationStore';
import { appDisplayName, groupLicensesByApp, isTestCustomer } from '@lib/customer';
import { parseExpiryDateInput } from '@lib/licenseUpdate';
import { FeatureFlagsManager } from '@components/FeatureFlagsManager';

const CustomerDetail: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();
  const [showEditCustomer, setShowEditCustomer] = useState(false);
  const [editCustomerData, setEditCustomerData] = useState({ name: '', email: '', state: 'active', is_test: false });
  const [selectedInstall, setSelectedInstall] = useState<string | null>(null);
  const [showFlagManager, setShowFlagManager] = useState(false);
  const [installFlagsMap, setInstallFlagsMap] = useState<Record<string, any[]>>({});
  const [installClaimCodesMap, setInstallClaimCodesMap] = useState<Record<string, any[]>>({});
  const [installDetailsMap, setInstallDetailsMap] = useState<Record<string, any | null>>({});
  const [showLinkInstall, setShowLinkInstall] = useState(false);
  const [linkInstallId, setLinkInstallId] = useState('');
  const [linkAppId, setLinkAppId] = useState('license_reclaim');
  const [showLinkClaimCode, setShowLinkClaimCode] = useState(false);
  const [linkClaimCode, setLinkClaimCode] = useState('');
  const [showMergeCustomer, setShowMergeCustomer] = useState(false);
  const [mergeLoserCustomerId, setMergeLoserCustomerId] = useState('');
  const [showNewContract, setShowNewContract] = useState(false);
  const [isSavingContract, setIsSavingContract] = useState(false);
  const [newContract, setNewContract] = useState({
    contract_type: 'startup',
    support_tier: 'standard',
    starts_at: '',
    ends_at: '',
    auto_renew: true,
    renewal_notice_days: 30,
    notes: '',
  });

  const { data: customerData, isLoading: customerLoading, refetch: refetchCustomer } = useQuery({
    queryKey: ['customer-detail', customerId],
    queryFn: async () => {
      return await apiClient.get<any>(`/api/customers/${customerId}?includeInstalls=true`);
    },
    enabled: !!customerId,
  });

  React.useEffect(() => {
    if (customerData) {
      setEditCustomerData({
        name: customerData.name || '',
        email: customerData.email || '',
        state: customerData.state || 'active',
        is_test: isTestCustomer(customerData),
      });
    }
  }, [customerData]);

  React.useEffect(() => {
    const fetchFlagsAndCodes = async () => {
      const licenses = customerData?.licenses || [];
      const flagsMap: Record<string, any[]> = {};
      const claimCodesMap: Record<string, any[]> = {};
      const detailsMap: Record<string, any | null> = {};

      for (const license of licenses) {
        try {
          const flags = await apiClient.get<any>(`/api/install-flags?install_id=${license.install_id}`);
          flagsMap[license.install_id] = Array.isArray(flags) ? flags : [];
        } catch (error) {
          console.error(`Failed to fetch flags for ${license.install_id}:`, error);
          flagsMap[license.install_id] = [];
        }

        // Fetch claim codes for this install
        try {
          const claimCodesResponse = await apiClient.get<any>(`/api/claim-codes-with-installs?app=${license.app_id}`);
          const codesForInstall = claimCodesResponse?.items?.filter(
            (cc: any) => cc.consumed_install_id === license.install_id
          ) || [];
          claimCodesMap[license.install_id] = codesForInstall;
        } catch (error) {
          console.error(`Failed to fetch claim codes for ${license.install_id}:`, error);
          claimCodesMap[license.install_id] = [];
        }

        // Fetch install status + license expiry from the control-plane proxy
        try {
          const detail = await apiClient.get<any>(`/api/installs?id=${license.install_id}`);
          detailsMap[license.install_id] = detail || null;
        } catch (error) {
          console.error(`Failed to fetch install detail for ${license.install_id}:`, error);
          detailsMap[license.install_id] = null;
        }
      }

      setInstallFlagsMap(flagsMap);
      setInstallClaimCodesMap(claimCodesMap);
      setInstallDetailsMap(detailsMap);
    };

    if (customerData?.licenses?.length) {
      fetchFlagsAndCodes();
    }
  }, [customerData?.licenses]);

  const { data: appsData } = useQuery({
    queryKey: ['apps-list'],
    queryFn: async () => {
      return await apiClient.get<any>('/api/apps?limit=100');
    },
  });
  const knownApps = appsData?.items || [];
  const appGroups = groupLicensesByApp(customerData?.licenses || []);

  const handleUpdateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.put(`/api/customers/${customerId}`, editCustomerData);
      setShowEditCustomer(false);
      refetchCustomer();
      addNotification({ message: 'Customer updated successfully', type: 'success' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update customer';
      addNotification({ message: `Error: ${errorMessage}`, type: 'error' });
      console.error('Failed to update customer:', error);
    }
  };

  const handleLinkInstall = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkInstallId.trim()) return;
    try {
      await apiClient.post(`/api/customers/${customerId}/installs`, {
        install_id: linkInstallId,
        app_id: linkAppId,
      });
      setLinkInstallId('');
      setShowLinkInstall(false);
      refetchCustomer();
      addNotification({
        message: `Install linked successfully`,
        type: 'success',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to link install';
      addNotification({
        message: `Error: ${errorMessage}`,
        type: 'error',
      });
      console.error('Failed to link install:', error);
    }
  };

  const handleUnlinkInstall = async (installId: string) => {
    if (!window.confirm('Unlink this install from the customer?')) return;
    try {
      await apiClient.delete(`/api/customers/${customerId}/installs?install_id=${installId}`);
      refetchCustomer();
      addNotification({
        message: `Install unlinked successfully`,
        type: 'success',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to unlink install';
      addNotification({
        message: `Error: ${errorMessage}`,
        type: 'error',
      });
      console.error('Failed to unlink install:', error);
    }
  };

  const handleLinkClaimCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkClaimCode.trim()) return;
    try {
      await apiClient.post(`/api/customers/${customerId}/claim-codes`, {
        code: linkClaimCode,
      });
      setLinkClaimCode('');
      setShowLinkClaimCode(false);
      refetchCustomer();
      addNotification({
        message: `Claim code linked successfully`,
        type: 'success',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to link claim code';
      addNotification({
        message: `Error: ${errorMessage}`,
        type: 'error',
      });
      console.error('Failed to link claim code:', error);
    }
  };

  const handleUnlinkClaimCode = async (code: string) => {
    if (!window.confirm('Unlink this claim code from the customer?')) return;
    try {
      await apiClient.delete(`/api/customers/${customerId}/claim-codes?code=${code}`);
      refetchCustomer();
      addNotification({
        message: `Claim code unlinked successfully`,
        type: 'success',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to unlink claim code';
      addNotification({
        message: `Error: ${errorMessage}`,
        type: 'error',
      });
      console.error('Failed to unlink claim code:', error);
    }
  };

  const handleCreateContract = async (e: React.FormEvent) => {
    e.preventDefault();
    const startsAt = parseExpiryDateInput(newContract.starts_at);
    const endsAt = parseExpiryDateInput(newContract.ends_at);
    if (startsAt === null || endsAt === null || endsAt <= startsAt) {
      addNotification({ message: 'Error: contract end date must be after the start date', type: 'error' });
      return;
    }
    setIsSavingContract(true);
    try {
      await apiClient.post('/api/contracts', {
        customer_id: customerId,
        contract_type: newContract.contract_type,
        support_tier: newContract.support_tier,
        starts_at: startsAt,
        ends_at: endsAt,
        auto_renew: newContract.auto_renew,
        renewal_notice_days: newContract.renewal_notice_days,
        notes: newContract.notes || undefined,
      });
      setShowNewContract(false);
      setNewContract({
        contract_type: 'startup',
        support_tier: 'standard',
        starts_at: '',
        ends_at: '',
        auto_renew: true,
        renewal_notice_days: 30,
        notes: '',
      });
      refetchCustomer();
      addNotification({ message: 'Contract created successfully', type: 'success' });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create contract';
      addNotification({ message: `Error: ${errorMessage}`, type: 'error' });
      console.error('Failed to create contract:', error);
    } finally {
      setIsSavingContract(false);
    }
  };

  const handleMergeCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mergeLoserCustomerId.trim()) return;
    if (!window.confirm(`Merge ${mergeLoserCustomerId} into ${customerId}? All claim codes, installs, and invoices will be transferred.`)) return;
    try {
      await apiClient.post('/api/customers/merge', {
        winner_id: customerId,
        loser_id: mergeLoserCustomerId,
      });
      setMergeLoserCustomerId('');
      setShowMergeCustomer(false);
      addNotification({
        message: `Customer merged successfully`,
        type: 'success',
      });
      // Navigate away since the loser customer is deleted
      navigate('/customers');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to merge customer';
      addNotification({
        message: `Error: ${errorMessage}`,
        type: 'error',
      });
      console.error('Failed to merge customer:', error);
    }
  };

  if (customerLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading customer...</div>
      </div>
    );
  }

  const customer = customerData || {};
  const contracts = customer?.contracts || [];
  const licenses = customer?.licenses || [];
  const claimCodes = customer?.claim_codes || [];
  const install = customer?.install;
  const license = customer?.license;

  return (
    <div>
      <button
        onClick={() => navigate('/customers')}
        className="mb-6 text-blue-600 hover:text-blue-800 underline text-sm"
      >
        ← Back to Customers
      </button>

      <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{customer?.name}</h1>
            <span
              className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                isTestCustomer(customer) ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
              }`}
            >
              {isTestCustomer(customer) ? 'Test' : 'Live'}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowEditCustomer(!showEditCustomer)}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
            >
              {showEditCustomer ? 'Cancel' : 'Edit'}
            </button>
            <button
              onClick={() => setShowMergeCustomer(!showMergeCustomer)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
            >
              Merge
            </button>
          </div>
        </div>

        {showMergeCustomer && (
          <form onSubmit={handleMergeCustomer} className="mb-4 bg-purple-50 p-4 rounded border border-purple-200">
            <p className="text-sm text-gray-700 mb-3">Merge another customer into this one. All claim codes, installs, invoices, and contracts will be transferred.</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={mergeLoserCustomerId}
                onChange={(e) => setMergeLoserCustomerId(e.target.value)}
                placeholder="Customer ID to merge into this one"
                className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
              >
                Merge
              </button>
            </div>
          </form>
        )}

        {showEditCustomer ? (
          <form onSubmit={handleUpdateCustomer} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={editCustomerData.name}
                onChange={(e) => setEditCustomerData({ ...editCustomerData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={editCustomerData.email}
                onChange={(e) => setEditCustomerData({ ...editCustomerData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="edit-customer-is-test"
                type="checkbox"
                checked={editCustomerData.is_test}
                onChange={(e) => setEditCustomerData({ ...editCustomerData, is_test: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="edit-customer-is-test" className="text-sm font-medium text-gray-700">
                Test customer (not live production)
              </label>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => setShowEditCustomer(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-2 gap-6">
            {/* Install Information */}
            {install && (
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900 text-sm">Install Information</h3>
                <div>
                  <span className="text-sm text-gray-600 block">Install ID</span>
                  <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{install.installId || customerId}</code>
                </div>
                {install.companyName && (
                  <div>
                    <span className="text-sm text-gray-600 block">Company</span>
                    <div className="text-sm font-medium">{install.companyName}</div>
                  </div>
                )}
                {install.sfOrgId && (
                  <div>
                    <span className="text-sm text-gray-600 block">Salesforce Org ID</span>
                    <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{install.sfOrgId}</code>
                  </div>
                )}
                {install.sfOrgType && (
                  <div>
                    <span className="text-sm text-gray-600 block">Org Type</span>
                    <div className="text-sm">{install.sfOrgType}</div>
                  </div>
                )}
                {install.appId && (
                  <div>
                    <span className="text-sm text-gray-600 block">Application</span>
                    <div className="text-sm font-medium">{install.appId}</div>
                  </div>
                )}
              </div>
            )}

            {/* Customer Information */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900 text-sm">Customer Information</h3>
              <div>
                <span className="text-sm text-gray-600 block">Registration Email</span>
                <div className="text-sm">{customer?.email || '—'}</div>
              </div>
              {customer?.phone && (
                <div>
                  <span className="text-sm text-gray-600 block">Phone</span>
                  <div className="text-sm">{customer.phone}</div>
                </div>
              )}
              {customer?.billing_contact_email && (
                <div>
                  <span className="text-sm text-gray-600 block">Billing Contact Email</span>
                  <div className="text-sm">{customer.billing_contact_email}</div>
                </div>
              )}
              {customer?.company_registration_id && (
                <div>
                  <span className="text-sm text-gray-600 block">Company Registration ID</span>
                  <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{customer.company_registration_id}</code>
                </div>
              )}
              <div>
                <span className="text-sm text-gray-600 block">State</span>
                <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                  customer?.state === 'active'
                    ? 'bg-green-100 text-green-800'
                    : customer?.state === 'trial'
                    ? 'bg-blue-100 text-blue-800'
                    : customer?.state === 'suspended'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {customer?.state || '—'}
                </span>
              </div>
            </div>

            {/* License Information */}
            {license && (
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900 text-sm">License Information</h3>
                <div>
                  <span className="text-sm text-gray-600 block">Tier</span>
                  <div className="text-sm font-medium">{license.tier}</div>
                </div>
                <div>
                  <span className="text-sm text-gray-600 block">State</span>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${license.state === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {license.state}
                  </span>
                </div>
              </div>
            )}

            {/* Metadata */}
            {customer?.metadata && (
              <div className="space-y-3">
                <h3 className="font-semibold text-gray-900 text-sm">Metadata</h3>
                <pre className="text-xs bg-gray-50 p-2 rounded border border-gray-200 overflow-auto max-h-48">
                  {typeof customer.metadata === 'string' ? customer.metadata : JSON.stringify(customer.metadata, null, 2)}
                </pre>
              </div>
            )}

            {/* Timestamps */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900 text-sm">Timestamps</h3>
              {customer?.created_at && (
                <div>
                  <span className="text-sm text-gray-600 block">Created At</span>
                  <div className="text-sm">{new Date(customer.created_at * 1000).toLocaleString()}</div>
                </div>
              )}
              {customer?.updated_at && (
                <div>
                  <span className="text-sm text-gray-600 block">Updated At</span>
                  <div className="text-sm">{new Date(customer.updated_at * 1000).toLocaleString()}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {install && (
        <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Applications Enabled</h2>
          <div className="flex gap-2 flex-wrap">
            {install?.appId && (
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                {install.appId}
              </div>
            )}
            {!install?.appId && (
              <div className="text-gray-500 text-sm">No applications enabled</div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Claim Codes Section */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-gray-900">Linked Claim Codes</h2>
            <button
              onClick={() => setShowLinkClaimCode(!showLinkClaimCode)}
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            >
              {showLinkClaimCode ? 'Cancel' : '+ Link'}
            </button>
          </div>

          {showLinkClaimCode && (
            <form onSubmit={handleLinkClaimCode} className="mb-4 bg-gray-50 p-4 rounded border border-gray-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={linkClaimCode}
                  onChange={(e) => setLinkClaimCode(e.target.value)}
                  placeholder="Claim code (e.g., ABC123-DEF456-GHI789-JKL012)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                >
                  Link
                </button>
              </div>
            </form>
          )}
          {claimCodes.length === 0 ? (
            <div className="bg-white rounded-lg p-4 border border-gray-200 text-center text-gray-500 text-sm">
              No claim codes linked yet
            </div>
          ) : (
            <div className="space-y-2">
              {claimCodes.map((code: any) => (
                <div
                  key={code.code}
                  className="bg-white rounded-lg p-3 border border-gray-200 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="font-mono text-sm font-medium">{code.code}</div>
                    <div className="text-xs text-gray-500">
                      {code.status} • {code.tier} • {code.seat_limit} seats
                    </div>
                  </div>
                  <button
                    onClick={() => handleUnlinkClaimCode(code.code)}
                    className="px-2 py-1 text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Unlink
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Installs/Licenses Section */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-gray-900">Applications & Installs</h2>
            <button
              onClick={() => setShowLinkInstall(!showLinkInstall)}
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            >
              {showLinkInstall ? 'Cancel' : '+ Link'}
            </button>
          </div>

          {showLinkInstall && (
            <form onSubmit={handleLinkInstall} className="mb-4 bg-gray-50 p-4 rounded border border-gray-200">
              <div className="space-y-2">
                <input
                  type="text"
                  value={linkInstallId}
                  onChange={(e) => setLinkInstallId(e.target.value)}
                  placeholder="Install ID (UUID)"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={linkAppId}
                  onChange={(e) => setLinkAppId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="license_reclaim">License Reclaim</option>
                  <option value="orbit">Orbit</option>
                  <option value="doc_route">DocRoute</option>
                </select>
                <button
                  type="submit"
                  className="w-full px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                >
                  Link Install
                </button>
              </div>
            </form>
          )}
          {licenses.length === 0 ? (
            <div className="bg-white rounded-lg p-4 border border-gray-200 text-center text-gray-500 text-sm">
              No installs linked yet
            </div>
          ) : (
            <div className="space-y-6">
              {appGroups.map((group) => (
                <div key={group.appId} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                    <div>
                      <span className="font-semibold text-gray-900">{appDisplayName(group.appId, knownApps)}</span>
                      <code className="ml-2 text-xs text-gray-500">{group.appId}</code>
                    </div>
                    <span className="text-xs text-gray-500">
                      {group.licenses.length} install{group.licenses.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {group.licenses.map((license: any, idx: number) => {
                      const claimCodesForInstall = installClaimCodesMap[license.install_id] || [];
                      const detail = installDetailsMap[license.install_id];
                      const installStatus = detail?.install?.status || license.install_status || 'unknown';
                      const expiresAt = detail?.license?.expires_at;
                      const isExpired = typeof expiresAt === 'number' && expiresAt * 1000 < Date.now();
                      const statusClass =
                        installStatus === 'active'
                          ? 'bg-green-100 text-green-800'
                          : installStatus === 'terminated'
                            ? 'bg-red-100 text-red-800'
                            : installStatus === 'suspended' || installStatus === 'expired'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800';
                      return (
                        <div key={idx} className="px-4 py-3">
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <div className="text-xs text-gray-500 mb-1">Install ID</div>
                              <code className="text-xs font-mono text-gray-900 break-all">
                                {license.install_id || 'unknown install'}
                              </code>
                              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                                <span>
                                  Status:{' '}
                                  <span className={`px-2 py-0.5 rounded font-medium ${statusClass}`}>
                                    {installStatus}
                                  </span>
                                </span>
                                <span>
                                  License expires:{' '}
                                  <span className={isExpired ? 'text-red-600 font-medium' : ''}>
                                    {typeof expiresAt === 'number'
                                      ? new Date(expiresAt * 1000).toLocaleDateString()
                                      : '—'}
                                  </span>
                                </span>
                                {license.admin_email && <span>{license.admin_email}</span>}
                              </div>
                              <div className="mt-2">
                                <div className="text-xs text-gray-500 mb-1">Claim codes</div>
                                {claimCodesForInstall.length === 0 ? (
                                  <span className="text-xs text-gray-400">—</span>
                                ) : (
                                  <div className="flex flex-wrap gap-2">
                                    {claimCodesForInstall.map((cc: any) => (
                                      <span
                                        key={cc.code}
                                        className="inline-flex items-center gap-1 text-xs font-mono bg-gray-50 border border-gray-200 rounded px-2 py-0.5"
                                      >
                                        {cc.code}
                                        <span className="font-sans text-gray-500">({cc.status})</span>
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-3 shrink-0">
                              {license.install_id && (
                                <button
                                  onClick={() => navigate(`/installs/${license.install_id}`)}
                                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                >
                                  Manage
                                </button>
                              )}
                              <button
                                onClick={() => handleUnlinkInstall(license.install_id)}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                              >
                                Unlink
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Feature Flags per Install */}
      {licenses.length > 0 && (
        <div className="space-y-6 mb-6">
          {licenses.map((license: any, idx: number) => (
            <div key={idx}>
              <div className="mb-2 text-xs text-gray-500 font-mono break-all">
                {license.app_id} • {license.install_id || 'unknown install'}
              </div>
              <FeatureFlagsManager
                installId={license.install_id}
                flags={installFlagsMap[license.install_id] || []}
                appId={license.app_id}
                onFlagsChange={() => {}}
                showManage={selectedInstall === license.install_id}
                onManageToggle={(show) => {
                  setSelectedInstall(show ? license.install_id : null);
                  setShowFlagManager(show);
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Contracts Section */}
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-900">Contracts</h2>
          <button
            onClick={() => setShowNewContract(!showNewContract)}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          >
            {showNewContract ? 'Cancel' : '+ New'}
          </button>
        </div>

        {showNewContract && (
          <form onSubmit={handleCreateContract} className="mb-4 bg-gray-50 p-4 rounded border border-gray-200">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contract Type</label>
                <select
                  value={newContract.contract_type}
                  onChange={(e) => setNewContract({ ...newContract, contract_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="startup">Startup</option>
                  <option value="growth">Growth</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Support Tier</label>
                <select
                  value={newContract.support_tier}
                  onChange={(e) => setNewContract({ ...newContract, support_tier: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="community">Community</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Starts</label>
                <input
                  type="date"
                  value={newContract.starts_at}
                  onChange={(e) => setNewContract({ ...newContract, starts_at: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ends</label>
                <input
                  type="date"
                  value={newContract.ends_at}
                  onChange={(e) => setNewContract({ ...newContract, ends_at: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Renewal Notice (days)</label>
                <input
                  type="number"
                  min={1}
                  value={newContract.renewal_notice_days}
                  onChange={(e) => setNewContract({ ...newContract, renewal_notice_days: parseInt(e.target.value, 10) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={newContract.auto_renew}
                    onChange={(e) => setNewContract({ ...newContract, auto_renew: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Auto-renew
                </label>
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <input
                type="text"
                value={newContract.notes}
                onChange={(e) => setNewContract({ ...newContract, notes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              disabled={isSavingContract}
              className="mt-3 px-4 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
            >
              {isSavingContract ? 'Saving…' : 'Create Contract'}
            </button>
          </form>
        )}

        {contracts.length === 0 && !showNewContract ? (
          <div className="text-center text-gray-500 text-sm">No contracts yet</div>
        ) : (
          <div className="space-y-2">
            {contracts.map((contract: any) => (
              <div key={contract.contract_id} className="flex items-center justify-between p-3 border border-gray-200 rounded">
                <div>
                  <div className="font-semibold text-gray-900">{contract.contract_type}</div>
                  <div className="text-sm text-gray-500">Support: {contract.support_tier}</div>
                </div>
                <div className="text-xs text-gray-500">{new Date(contract.created_at * 1000).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetail;
